import React, { Suspense, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from "react-router-dom";
import Class from "better-classnames";
import { AnnounceProvider } from "./components/providers/announce";
import {
  SessionContext,
  SessionProvider,
} from "./components/providers/session";

import "./styles/tailwind/tailwind.css";
import "./styles/fonts.scss";
import "./styles/index.scss";

import ErrorBoundary from "./components/common/errorBoundary";

import "./resources/i18n";
import reportWebVitals from "./resources/reportWebVitals";
import ErrorProblemIllustration from "./pages/error/img/errorProblem.svg";
import { useBreakPoint, lg } from "./util/screenBreakpoints";

import { enRoutes, frRoutes, getRoute } from "./resources/routes";
import {
  FormProvider,
  useForm,
} from "./components/providers/form";
import { AuthProvider, useAuth } from "./components/providers/auth";
import lazyWithPreload from "./util/lazyWithPreload";
import Loader from "./components/common/loader";

const Login = lazyWithPreload(() => import("./pages/login/login"), true);
const UserInfo = lazyWithPreload(() => import("./pages/userInfo/userInfo"), true);
const EmployeeInfo = lazyWithPreload(() => import("./pages/employeeInfo/employeeInfo"), true);
const Banking = lazyWithPreload(() => import("./pages/banking/banking"), true);
const Submit = lazyWithPreload(() => import("./pages/submit/submit"), true);
const Success = lazyWithPreload(() => import("./pages/submit/success"), true);
const Failed = lazyWithPreload(() => import("./pages/submit/failed"), true);
const Error = lazyWithPreload(() => import("./pages/error/error"), true);
const Invalid = lazyWithPreload(() => import("./pages/error/invalid"), true);
const Limit = lazyWithPreload(() => import("./pages/error/limit"), true);
const NotFound = lazyWithPreload(() => import("./pages/notFound"), true);

const Palette = lazyWithPreload(() => import("./pages/palette"), true);

const Loading: React.FC = () => (
  <div id="loading-info" className="w-full h-full flex flex-col content-center">
    <h1 className="mx-auto mt-20 text-xl border-b-0 text-center">
      Loading... / Chargement...
    </h1>
  </div>
);

const ErrorFallback: React.FC = () => {
  const bigScreen = useBreakPoint(lg);
  return (
    <div className="h-full pb-12 grid grid-cols-12 gap-4 items-start">
      {bigScreen && (
        <div className="col-span-6  flex flex-col my-auto">
          <img
            src={ErrorProblemIllustration}
            alt="Laptop with warning sign on screen / Ordinateur portable avec panneau d'avertissement à l'écran"
            className="max-w-full m-auto"
          />
        </div>
      )}
      <div
        className={Class("my-auto mx-4", {
          "col-span-6": bigScreen,
          "col-span-12": "_DEFAULT_",
        })}
      >
        <div className="mx-auto">
          <h1 className="border-b-0 m-0 p-0">
            We’re experiencing a technical difficulty
          </h1>
          <p className="my-4">
            We’re working to fix this as soon as possible. Try refreshing the
            page or come back later.
          </p>
          <h1 className="border-b-0 mt-8 p-0">
            Nous éprouvons des difficultés techniques
          </h1>
          <p className="my-4">
            Nous nous efforçons de régler le problème le plus tôt possible. Vous
            pouvez rafraîchir cette page ou essayer à nouveau plus tard.
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Application routing and root level configuration
 */
const App: React.FC = () => {
  const {
    auth,
    token,
    logout,
    inviteStatus,
    isLoggingOut
  } = useAuth();

  const {
    checkInvite,
    userType,
    error,
  } = useForm();

  useEffect(() => {
    window.onbeforeunload = () => {
      if (token) {
        logout( true);
      }
    };
  }, [token, logout]);

  if(isLoggingOut){
    return <Loader color="light"/>
  }

  return (
    <Router>
      <Switch>
        <Route
          exact={true}
          path={[enRoutes.login, frRoutes.login]}
          render={(props) => {
            checkInvite(props.match.params.invite as string);
            return inviteStatus === "invalid" ? <Invalid /> : 
              inviteStatus === "limit" ? <Limit /> : 
              inviteStatus === "error" ? <Error /> : 
              userType ? <Login /> : <Loading/>
          }}
        />
        <Route
          exact={true}
          path={[enRoutes.error, frRoutes.error]}
          component={Error}
        />
        <Route
          exact={true}
          path={[enRoutes.userInfo, frRoutes.userInfo]}
          render={(props) => {
            return auth ? (<UserInfo {...props}/>)
            : (<Redirect to={getRoute("root")} />);
          }}
        />
         <Route
          exact={true}
          path={[enRoutes.employeeInfo, frRoutes.employeeInfo]}
          render={(props) => {
            return auth ? (<EmployeeInfo {...props}/>)
            : (<Redirect to={getRoute("root")} />);
          }}
        />
        <Route
          exact={true}
          path={[enRoutes.banking, frRoutes.banking]}
          render={(props) => {
            return auth ? (<Banking {...props} />)
            : (<Redirect to={getRoute("root")} />);
          }}
        />
        <Route
          exact={true}
          path={[enRoutes.submit, frRoutes.submit]}
          render={(props) => {
            return auth ? (<Submit {...props} />)
            : (<Redirect to={getRoute("root")} />);
          }}
        />
        <Route
          exact={true}
          path={[enRoutes.success, frRoutes.success]}
          component={Success}
        />
        <Route
          exact={true}
          path={[enRoutes.failed, frRoutes.failed]}
          component={Failed}
        />
        <Route
          exact={true}
          path={[enRoutes.root, frRoutes.root]}
          component={NotFound}
        />
        <Route
          exact={true}
          path={[enRoutes.root, frRoutes.root]}
          component={NotFound}
        />
        <Route
          exact={true}
          path={[enRoutes.root, frRoutes.root]}
          component={NotFound}
        />
        <Route
          exact={true}
          path="/palette"
          component={Palette}
        />
        <Route
          exact={true}
          path="/"
          component={() => <Redirect to={getRoute("root")} />}
        />
        <Route component={NotFound} />
      </Switch>
    </Router>
  );
};
ReactDOM.render(
  <React.StrictMode>
    <ErrorBoundary fallback={<ErrorFallback />}>
      <Suspense fallback={<Loading />}>
        <AnnounceProvider>
          <SessionProvider>
            <SessionContext.Consumer>
              {({ config }) =>
                config ? ( // banklist is provided from SessionContext but not used here because the app should still load
                  <AuthProvider>
                    <FormProvider>
                      <App />
                    </FormProvider>
                  </AuthProvider>
                ) : (
                  <Loading />
                )
              }
            </SessionContext.Consumer>
          </SessionProvider>
        </AnnounceProvider>
      </Suspense>
    </ErrorBoundary>
  </React.StrictMode>,
  document.getElementById("root")
);

reportWebVitals();
